'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LatestFullRescanResponse } from '@terra/shared-contracts-applications';
import { terraProxyApiClient } from '../../clients/testerClient';

export const LATEST_APPLICATION_FULL_RESCAN_QUERY_KEY =
  'latest-application-full-rescan';

const ACTIVE_RESCAN_REFETCH_INTERVAL_MS = 60_000;

export function useLatestApplicationFullRescan({
  applicationId,
  enabled = true,
}: {
  applicationId: string | undefined;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [LATEST_APPLICATION_FULL_RESCAN_QUERY_KEY, applicationId],
    queryFn: () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return terraProxyApiClient.getLatestApplicationFullRescan(applicationId);
    },
    enabled: enabled && Boolean(applicationId),
    refetchInterval: (query) =>
      query.state.data?.run?.active ? ACTIVE_RESCAN_REFETCH_INTERVAL_MS : false,
  });
}

export function useStartApplicationFullRescan({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return terraProxyApiClient.startApplicationFullRescan(applicationId);
    },
    onSuccess: (run) => {
      queryClient.setQueryData<LatestFullRescanResponse>(
        [LATEST_APPLICATION_FULL_RESCAN_QUERY_KEY, applicationId],
        { enabled: true, run },
      );
    },
  });
}
